// src/components/ActivityHeartbeat.js
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const LOGOUT_TIMER_MS = 30 * 60 * 1000; // Log out after 30 minutes with no detected activity.
const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // Consider user idle after 5 minutes with no input
const HEARTBEAT_SEND_INTERVAL_MS = 15 * 1000; // Flush active time every 15s
const ACTIVITY_ACCUMULATION_TICK_MS = 1000; // 1s granularity


const STATIC_PUBLIC_ROUTES = new Set([
  '',
  'register-interest',
  'privacy-policy',
  'login',
  'nacro-login',
  'demo',
  'dashboard',
  'lite',
  'lessons',
  'students',
  'profiles',
  'content-library',
  'lesson-plan',
  'homework',
  'student-dashboard',
  'extraction-report',
  'content-mapping',
  'tutorial',
  'slideshow',
  'student-slideshow',
  'student-tutorial-player',
  'share',
  'tutorial-report',
  'cost-tracker',
  'class-link',
  'vocationaldemo'
]);

const isLikelySharedLinkPath = (pathname = '') => {
  const safePath = String(pathname || '').trim();
  if (!safePath || safePath === '/') return false;

  const [segment] = safePath.replace(/^\//, '').split('/');
  if (!segment) return false;

  return !STATIC_PUBLIC_ROUTES.has(segment);
};

const isSharedLinkPath = (pathname = '') => String(pathname || '').startsWith('/share/') || isLikelySharedLinkPath(pathname);

const ActivityHeartbeat = () => {
  const lastInteractionAtRef = useRef(Date.now());
  const lastTickAtRef = useRef(Date.now());
  const accumulatedActiveMsRef = useRef(0);
  const logoutTimerRef = useRef(null);

  useEffect(() => {
    const clearLogoutTimer = () => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
    };

    const scheduleLogoutTimer = () => {
      clearLogoutTimer();
      const remaining = LOGOUT_TIMER_MS - (Date.now() - lastInteractionAtRef.current);
      if (remaining <= 0) {
        if (auth.currentUser) {
          console.log('💤 User idle for too long. Logging out...');
          signOut(auth).then(() => { window.location.href = '/login'; });
        }
        return;
      }
      logoutTimerRef.current = setTimeout(async () => {
        if (auth.currentUser) {
          console.log('💤 User idle for too long. Logging out...');
          await signOut(auth);
          window.location.href = '/login';
        }
      }, remaining);
    };

    const markInteraction = () => {
      lastInteractionAtRef.current = Date.now();
      scheduleLogoutTimer();
    };

    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, markInteraction, { passive: true }));

    const accumulateTimer = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.max(0, now - lastTickAtRef.current);
      lastTickAtRef.current = now;

      const isVisible = document.visibilityState === 'visible';
      const notIdle = (now - lastInteractionAtRef.current) <= IDLE_TIMEOUT_MS;
      const isSharedTutorial = isSharedLinkPath(window.location.pathname);

      if (isSharedTutorial) {
        accumulatedActiveMsRef.current = 0;
        return;
      }

      if (isVisible && notIdle) {
        accumulatedActiveMsRef.current += elapsed;
      }
    }, ACTIVITY_ACCUMULATION_TICK_MS);

    const heartbeatTimer = setInterval(async () => {
      if (isSharedLinkPath(window.location.pathname)) {
        accumulatedActiveMsRef.current = 0;
        return;
      }

      if (!auth.currentUser) return;

      const activeMs = Math.round(accumulatedActiveMsRef.current);
      if (activeMs <= 0) return;

      try {
        const token = await auth.currentUser.getIdToken();
        await axios.post(
          `${API_BASE_URL}/api/activity/heartbeat`,
          { activeMs },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        accumulatedActiveMsRef.current = 0;
      } catch (err) {
        console.error('Heartbeat failed', err);
      }
    }, HEARTBEAT_SEND_INTERVAL_MS);

    const handleVisibilityChange = () => {
      // Reset tick baseline so hidden tab time is not accidentally accumulated on resume.
      lastTickAtRef.current = Date.now();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
      clearLogoutTimer();
      if (!firebaseUser) return;
      lastInteractionAtRef.current = Date.now();
      lastTickAtRef.current = Date.now();
      accumulatedActiveMsRef.current = 0;
      scheduleLogoutTimer();
    });

    return () => {
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, markInteraction));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(accumulateTimer);
      clearInterval(heartbeatTimer);
      unsubscribeAuth();
      clearLogoutTimer();
    };
  }, []);

  return null;
};

export default ActivityHeartbeat;
